import { useState } from 'react';

import { TIdVerificationPerson } from '@helpers/FundingRequirements/fundingRequirements.types';
import { Stack, Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { IdCheckPersonForm, IdCheckPersonStatus } from './IdCheckPerson';
import { IdCheckSmsSendError } from './IdCheckSmsSendError';
import { TSendIdCheckSmsParams, useSendIdCheckSms } from './useSendIdCheckSms';

const getCanSend = (person: TIdVerificationPerson, sendingAgainUid?: string) =>
    person.status === 'action_needed' ||
    person.status === 'error' ||
    person.uid === sendingAgainUid;

export const SendIdCheckSms = ({
    people,
    sendingAgainUid,
    onSent
}: {
    people: TIdVerificationPerson[];
    sendingAgainUid?: string;
    onSent: (params: TSendIdCheckSmsParams) => void;
}) => {
    const [isRetrying, setIsRetrying] = useState(false);
    const { send, retry, sendingPersonUid, isSending, isSendError } =
        useSendIdCheckSms({ onSent });
    const isMultiple = people.length > 1;

    // Stay on the error screen while retrying, rather than flashing back to the form.
    if (isSendError || (isRetrying && isSending)) {
        return (
            <IdCheckSmsSendError
                isRetrying={isSending}
                onRetry={() => {
                    setIsRetrying(true);
                    retry();
                }}
            />
        );
    }

    return (
        <Stack gap="section">
            <Stack>
                <Text className="text-3xl font-bold">
                    <FormattedMessage
                        id="_.funding_requirements_v2.id_verification.title"
                        defaultMessage="Verify your identity"
                    />
                </Text>
                <Text className="text-xl">
                    <FormattedMessage
                        id="_.funding_requirements_v2.id_verification.subtitle"
                        defaultMessage="For your protection, we need to make sure it’s really you. This way, the transfer doesn’t go to the wrong person."
                    />
                </Text>
            </Stack>

            <Stack gap="comfortable">
                <div>
                    <Text>
                        <FormattedMessage
                            id="_.funding_requirements_v2.id_verification.documents_intro"
                            defaultMessage="Have one of the following documents ready:"
                        />
                    </Text>
                    <ul className="list-disc">
                        <li className="ms-xl">
                            <FormattedMessage
                                id="_.funding_requirements_v2.id_verification.document_id_card"
                                defaultMessage="ID card"
                            />
                        </li>
                        <li className="ms-xl">
                            <FormattedMessage
                                id="_.funding_requirements_v2.id_verification.document_passport"
                                defaultMessage="Passport"
                            />
                        </li>
                        <li className="ms-xl">
                            <FormattedMessage
                                id="_.funding_requirements_v2.id_verification.document_driving_licence"
                                defaultMessage="Driving licence"
                            />
                        </li>
                    </ul>
                </div>
                <Text>
                    {isMultiple ? (
                        <FormattedMessage
                            id="_.funding_requirements_v2.id_verification.sms_instruction_multiple"
                            defaultMessage="Verification is done on your smartphone. Send the next steps to everyone involved:"
                        />
                    ) : (
                        <FormattedMessage
                            id="_.funding_requirements_v2.id_verification.sms_instruction"
                            defaultMessage="Verification is done on your smartphone. Get the next steps by SMS to your mobile number:"
                        />
                    )}
                </Text>
            </Stack>

            <Stack gap="section">
                {people.map((person) =>
                    !isMultiple || getCanSend(person, sendingAgainUid) ? (
                        <IdCheckPersonForm
                            key={person.uid}
                            person={person}
                            showName={isMultiple}
                            isSending={sendingPersonUid === person.uid}
                            onSend={(phone) =>
                                send({ personUid: person.uid, phone })
                            }
                        />
                    ) : (
                        <IdCheckPersonStatus key={person.uid} person={person} />
                    )
                )}
            </Stack>
        </Stack>
    );
};
